import type { RequestDocument, Variables } from "graphql-request";
import { ClientError } from "graphql-request";

export type GraphQLErrorDetails = Array<{
  message: string;
  code: string;
}>;

export type ErrorDetails = {
  message: string;
  code: string | number;
  path?: readonly (string | number)[];
  extensions?: {
    category?: string;
  };
};

export class MagentoGraphQLRequestClientError<V = Variables> extends Error {
  variables: string;
  errorCodes: string[] | undefined;
  errorsDetails?: ErrorDetails[];
  response?: ClientError["response"];
  request?: ClientError["request"];
  debuggingPayload: GraphQLErrorDetails;

  private static unknownErrorLabel = "Unknown GraphQL error";

  constructor(
    error: ClientError | unknown,
    public document?: RequestDocument,
    variables?: V
  ) {
    super();

    const isClientError = MagentoGraphQLRequestClientError.isGraphQLRequestError(error);

    this.message = isClientError ? error.message : MagentoGraphQLRequestClientError.unknownErrorLabel;

    if (isClientError) {
      this.response = error.response;
      this.request = error.request;
    }

    const errorsDetails = (error as any)?.response?.errors;

    this.name = "MagentoGraphQLRequestClientError";
    this.variables = JSON.stringify(variables) ?? "";
    this.errorCodes = errorsDetails?.map((e: any) => String(e.code));
    this.errorsDetails = errorsDetails;

    this.debuggingPayload = isClientError
      ? (errorsDetails ?? []).map((error: any) => ({
          message: error?.message,
          code: error?.code,
        }))
      : [{ message: MagentoGraphQLRequestClientError.unknownErrorLabel, code: null }];
  }

  static isGraphQLRequestError(error: unknown): error is ClientError {
    return error instanceof ClientError;
  }

  static isMagentoGraphQLRequestClientError(error: unknown): error is MagentoGraphQLRequestClientError {
    return error instanceof MagentoGraphQLRequestClientError;
  }

  log() {
    console.error("GraphQL error has happened, examine the details below");

    console.table(this.debuggingPayload);
  }
}
